// Сметките зад справка „По сервитьори".
//
// BE връща две неща за периода: `summary` (по сервитьор) и `data` (по
// сервитьор И ден). Оттук излизат редовият модел на таблицата, редовете на
// детайла по дни и обобщението за кутийките.
//
// Разбивката по плащане е КАТО в „Продажби по периоди": смесеното плащане
// няма своя колона, а е разпределено в „В брой" и „С карта". Затова тези два
// метода идват от `totalCash`/`totalCard` (BE ги разпределя по сумите на
// смесеното), а останалите — от `methods[]`, където смесеното стои цяло и тук
// се прескача. Така „В брой" е числото, по което сервитьорът предава касата, а
// сборът на методите е оборотът. Кои методи стават колони решава
// `buildMethodEntries` — всички активни + неактивните с пари в периода.

import { parseDecimal } from "../../../../components/dataTable";
import type {
  WaiterDataItem,
  WaiterMethodAmount,
  WaiterSummaryItem,
} from "../../../../api/reportsWaiters";

// Bill.payment_method_id на системните методи (BE: PAYMENT_METHOD_*).
const CASH_PAYMENT_METHOD_ID = 1;
const CARD_PAYMENT_METHOD_ID = 2;
export const MIXED_PAYMENT_METHOD_ID = 3;

/** Ред в таблицата — един сервитьор за целия период. */
export type WaiterRow = {
  /**
   * Ключът е име + позиция, не име: `summary` се групира по id на сервитьор,
   * а двама различни потребители могат да носят едно и също изписано име.
   */
  rowKey: string;
  /** `null`, когато името сочи към повече от един (или към нито един) id. */
  waiterId: number | null;
  waiterName: string;
  bills: number;
  total: number;
  average: number;
  /**
   * Оборот по метод със смесеното РАЗПРЕДЕЛЕНО в „В брой" и „С карта";
   * липсващият метод е 0, за да сумира редът „Общо".
   */
  byMethod: Record<number, number>;
  /**
   * Брой сметки по метод — само за `count` на лентата по методи; колона за
   * него нарочно няма. Смесената сметка се брои и при „В брой", и при „С карта".
   */
  countByMethod: Record<number, number>;
  discounts: number;
  /** Надбавките се държат отделно — иначе нетото се рисува с двоен минус. */
  surcharges: number;
  voids: number;
  /** Дял от оборота на периода, в проценти. */
  share: number;
  /** Оборотът на целия период — лентата в клетката иска своя знаменател. */
  periodTotal: number;
};

/** Ред в детайла — един ден на избрания сервитьор. */
export type WaiterDayRow = {
  rowKey: string;
  /** Готов за показване „15.07.2026" — форматиран РЪЧНО, не с bg-BG locale. */
  dateLabel: string;
  /** Денят от седмицата; зависи от езика, затова влиза в самия ред. */
  weekday: string;
  /**
   * Денят като момент. Колоната „Дата" носи ОБЕКТ и общият компаратор я
   * сортира по показания текст — „01.08" би застанало пред „31.07". Оттук
   * идва истинската подредба.
   */
  timestamp: number;
  bills: number;
  total: number;
  average: number;
  /** Като в реда на сервитьора — смесеното е в „В брой" и „С карта". */
  byMethod: Record<number, number>;
};

/** Обобщението за лентата с кутийки. */
export type WaitersSummary = {
  total: number;
  bills: number;
  waiters: number;
  averagePerWaiter: number;
  byMethod: Record<number, number>;
  countByMethod: Record<number, number>;
};

/**
 * Сумите по метод от един запис. Броят сметки е сборът на бройките в
 * `methods[]` — там всяка сметка е точно веднъж, включително смесената.
 */
const foldMethods = ({
  methods,
  cash,
  card,
  cashCount,
  cardCount,
  mixedCount,
}: {
  methods: WaiterMethodAmount[] | undefined;
  /** Кешът и картата със смесеното вътре (`totalCash`/`cash_amount`). */
  cash: number;
  card: number;
  cashCount: number;
  cardCount: number;
  mixedCount: number;
}): {
  byMethod: Record<number, number>;
  countByMethod: Record<number, number>;
  bills: number;
} => {
  const byMethod: Record<number, number> = {};
  const countByMethod: Record<number, number> = {};
  let bills = 0;
  for (const method of methods ?? []) {
    const id = method.payment_method_id;
    const count = parseDecimal(method.count);
    bills += count;
    // Кешът и картата идват наготово със смесеното вътре; смесеното само по
    // себе си колона няма.
    if (
      id === CASH_PAYMENT_METHOD_ID ||
      id === CARD_PAYMENT_METHOD_ID ||
      id === MIXED_PAYMENT_METHOD_ID
    ) {
      continue;
    }
    byMethod[id] = (byMethod[id] ?? 0) + parseDecimal(method.amount);
    countByMethod[id] = (countByMethod[id] ?? 0) + count;
  }
  if (cash || cashCount || mixedCount) {
    byMethod[CASH_PAYMENT_METHOD_ID] = cash;
    countByMethod[CASH_PAYMENT_METHOD_ID] = cashCount + mixedCount;
  }
  if (card || cardCount || mixedCount) {
    byMethod[CARD_PAYMENT_METHOD_ID] = card;
    countByMethod[CARD_PAYMENT_METHOD_ID] = cardCount + mixedCount;
  }
  return { byMethod, countByMethod, bills };
};

/**
 * Име на сервитьор → неговото id, взето от дневните записи (`summary` носи
 * само име). Двусмислените имена нарочно НЕ влизат: приписването на чужди
 * отстъпки и сторно на съименник е по-лошо от липсващо число.
 */
export const buildWaiterIdByName = (
  items: readonly WaiterDataItem[],
): Map<string, number> => {
  const idsByName = new Map<string, Set<number>>();
  for (const item of items) {
    const ids = idsByName.get(item.waiterName) ?? new Set<number>();
    ids.add(item.waiterId);
    idsByName.set(item.waiterName, ids);
  }
  const resolved = new Map<string, number>();
  for (const [name, ids] of idsByName) {
    if (ids.size === 1) resolved.set(name, [...ids][0]);
  }
  return resolved;
};

export const buildWaiterRows = ({
  summaries,
  waiterIdByName,
  discountsByWaiter,
  surchargesByWaiter,
  voidsByWaiter,
}: {
  summaries: readonly WaiterSummaryItem[];
  waiterIdByName: Map<string, number>;
  /** Сервитьорът НА СМЕТКАТА → дадени отстъпки (само положителните). */
  discountsByWaiter: Map<number, number>;
  /** Същото за надбавките, като ПОЛОЖИТЕЛНО число. */
  surchargesByWaiter: Map<number, number>;
  /** `waiter_id` → сторнирана сума. */
  voidsByWaiter: Map<number, number>;
}): WaiterRow[] => {
  const rows = summaries.map((summary, index) => {
    const { byMethod, countByMethod, bills } = foldMethods({
      methods: summary.methods,
      cash: parseDecimal(summary.totalCash),
      card: parseDecimal(summary.totalCard),
      cashCount: parseDecimal(summary.countCash),
      cardCount: parseDecimal(summary.countCard),
      mixedCount: parseDecimal(summary.countMixed),
    });
    const total = parseDecimal(summary.total);
    const waiterId = waiterIdByName.get(summary.waiterName) ?? null;
    return {
      rowKey: `${summary.waiterName}#${index}`,
      waiterId,
      waiterName: summary.waiterName,
      bills,
      total,
      // Сметките с 0.00 не влизат в средната.
      average: summary.countPaid > 0 ? total / summary.countPaid : 0,
      byMethod,
      countByMethod,
      discounts: waiterId === null ? 0 : (discountsByWaiter.get(waiterId) ?? 0),
      surcharges:
        waiterId === null ? 0 : (surchargesByWaiter.get(waiterId) ?? 0),
      voids: waiterId === null ? 0 : (voidsByWaiter.get(waiterId) ?? 0),
      share: 0,
      periodTotal: 0,
    };
  });

  const periodTotal = rows.reduce((sum, row) => sum + row.total, 0);
  return rows.map((row) => ({
    ...row,
    periodTotal,
    share: periodTotal > 0 ? (row.total / periodTotal) * 100 : 0,
  }));
};

export const summarizeWaiters = (rows: readonly WaiterRow[]): WaitersSummary => {
  const byMethod: Record<number, number> = {};
  const countByMethod: Record<number, number> = {};
  let total = 0;
  let bills = 0;
  for (const row of rows) {
    total += row.total;
    bills += row.bills;
    for (const [id, amount] of Object.entries(row.byMethod)) {
      byMethod[Number(id)] = (byMethod[Number(id)] ?? 0) + amount;
    }
    for (const [id, count] of Object.entries(row.countByMethod)) {
      countByMethod[Number(id)] = (countByMethod[Number(id)] ?? 0) + count;
    }
  }
  return {
    total,
    bills,
    waiters: rows.length,
    averagePerWaiter: rows.length > 0 ? total / rows.length : 0,
    byMethod,
    countByMethod,
  };
};

/** Сумите по метод във вида, който иска `buildMethodEntries`. */
export const methodTotalsOfSummary = (
  summary: WaitersSummary,
): Map<number, { amount: number; count: number }> =>
  new Map(
    Object.entries(summary.byMethod).map(([id, amount]) => [
      Number(id),
      { amount, count: summary.countByMethod[Number(id)] ?? 0 },
    ]),
  );

/**
 * Дните на един сервитьор, най-новият отгоре. `formatDay` идва отвън, защото
 * денят от седмицата зависи от езика и трябва да е в реда, не в accessor-а.
 */
export const buildWaiterDayRows = (
  items: readonly WaiterDataItem[],
  formatDay: (date: string) => { dateLabel: string; weekday: string },
): WaiterDayRow[] =>
  [...items]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .map((item) => {
      const { byMethod, bills } = foldMethods({
        methods: item.methods,
        cash: parseDecimal(item.cash_amount),
        card: parseDecimal(item.card_amount),
        cashCount: parseDecimal(item.cash_count),
        cardCount: parseDecimal(item.card_count),
        mixedCount: parseDecimal(item.mixed_count),
      });
      const total = parseDecimal(item.total);
      return {
        rowKey: `${item.waiterId}-${item.date}`,
        ...formatDay(item.date),
        timestamp: new Date(item.date).getTime(),
        bills,
        total,
        // Сметките с 0.00 не влизат в средната.
        average: item.paid_count > 0 ? total / item.paid_count : 0,
        byMethod,
      };
    });
